import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import subsidiariesApi from 'services/api/subsidiaries'
import { LandingPageDomainPreference } from 'utils/enums'

import ConfirmationDialog from '../../../components/ReusableComponents/ConfirmationDialog/index'
import styles from './EditSubDomainControlDialog.module.scss'

const EditSubDomainControlDialog = ({
  subdomain: subdomainFromProps,
  domain: domainFromProps,
  landingPageDomainPreference: landingPageDomainPreferenceFromProps,
  defaultChainSubdomain,
  handleClose,
  handleConfirm
}) => {
  const { addToast } = useToasts()

  const [subdomain, setSubdomain] = useState(subdomainFromProps)
  const [domain, setDomain] = useState(domainFromProps)
  const [landingPageDomainPreference, setLandingPageDomainPreference] = useState(
    landingPageDomainPreferenceFromProps
  )
  const [customSubdomainPlusDomain, setCustomSubdomainPlusDomain] = useState(
    landingPageDomainPreferenceFromProps === LandingPageDomainPreference.Domain
      ? `${subdomain}.${domain}`
      : ''
  )

  const [showCustomSubdomainModal, setShowCustomSubdomainModal] = useState(false)
  const [showCustomDomainModal, setShowCustomDomainModal] = useState(false)

  const handleCustomDomainConfirm = async () => {
    try {
      // Check if the customSubdomainPlusDomain is following the format a.b.c
      // where a is the subdomain and b.c is the domain
      if (!customSubdomainPlusDomain.includes('.')) {
        addToast('Please enter a valid domain', {
          appearance: 'error',
          autoDismiss: true
        })
        return
      } else if (customSubdomainPlusDomain.split('.').length !== 3) {
        addToast('Please enter a valid domain', {
          appearance: 'error',
          autoDismiss: true
        })
        return
      }

      const [subdomain, domain, tld] = customSubdomainPlusDomain.split('.')

      const result = await subsidiariesApi.domain.checkDnsRecord({
        domain: customSubdomainPlusDomain
      })

      if (!result) {
        addToast('DNS propagation is not completed, please try again later', {
          appearance: 'error',
          autoDismiss: true
        })
        return
      }

      handleConfirm({
        subdomain,
        domain: `${domain}.${tld}`,
        landingPageDomainPreference
      })
    } catch (error) {
      addToast(error.message, {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  return (
    <ConfirmationDialog
      title="Guest landing page URL"
      theme="blacklist"
      positiveMessage="Save changes"
      onClose={handleClose}
      negativeMessage="Cancel"
      onConfirm={() =>
        handleConfirm({
          subdomain,
          domain,
          landingPageDomainPreference
        })
      }
    >
      <div className={styles.container}>
        <label
          className={
            landingPageDomainPreference === LandingPageDomainPreference.Default
              ? styles.barBlue
              : styles.bar
          }
        >
          <div>
            <input
              type="radio"
              name="subDomainOption"
              checked={
                landingPageDomainPreference === LandingPageDomainPreference.Default
              }
              onChange={() =>
                setLandingPageDomainPreference(LandingPageDomainPreference.Default)
              }
            />
            <span>Use brand URL</span>
          </div>
          <span className={styles.domainOption}>
            {defaultChainSubdomain}.directful.com
          </span>
        </label>

        <label
          className={
            landingPageDomainPreference === LandingPageDomainPreference.Subdomain
              ? styles.barBlue
              : styles.bar
          }
        >
          <div>
            <input
              type="radio"
              name="subDomainOption"
              checked={
                landingPageDomainPreference === LandingPageDomainPreference.Subdomain
              }
              onChange={() => {
                setLandingPageDomainPreference(LandingPageDomainPreference.Subdomain)
                setSubdomain('')
                setShowCustomSubdomainModal(true)
              }}
            />
            <span>Use a custom subdomain</span>
          </div>
          <span className={styles.domainOption}>custom.directful.com</span>
        </label>

        <label
          className={
            landingPageDomainPreference === LandingPageDomainPreference.Domain
              ? styles.barBlue
              : styles.bar
          }
        >
          <div>
            <input
              type="radio"
              name="subDomainOption"
              checked={landingPageDomainPreference === LandingPageDomainPreference.Domain}
              onChange={() => {
                setLandingPageDomainPreference(LandingPageDomainPreference.Domain)
                setSubdomain('')
                setDomain('')
                setShowCustomDomainModal(true)
              }}
            />
            <span>Use your own domain</span>
          </div>
          <span className={styles.domainOption}>brand.yourdomain.com</span>
        </label>

        {showCustomSubdomainModal && (
          <ConfirmationDialog
            title="Use a custom subdomain"
            theme="blacklist"
            positiveMessage="Save"
            onClose={() => {
              setShowCustomSubdomainModal(false)
              setSubdomain(subdomainFromProps)
              setDomain(domainFromProps)
              setLandingPageDomainPreference(landingPageDomainPreferenceFromProps)
            }}
            negativeMessage="Cancel"
            onConfirm={() =>
              handleConfirm({
                subdomain,
                domain,
                landingPageDomainPreference
              })
            }
          >
            <div className={styles.container}>
              <div className={styles.customInput}>
                <TextInput
                  label="Enter a custom subdomain"
                  value={subdomain || ''}
                  placeholder="yourbrand"
                  onChange={e => setSubdomain(e.target.value)}
                  className={styles.inputText}
                />
                <span className={styles.helperText}>.directful.com</span>
              </div>
            </div>
          </ConfirmationDialog>
        )}

        {showCustomDomainModal && (
          <ConfirmationDialog
            title="Use your own subdomain"
            theme="blacklist"
            positiveMessage="Test & Save"
            onClose={() => {
              setShowCustomDomainModal(false)
              setSubdomain(subdomainFromProps)
              setDomain(domainFromProps)
              setLandingPageDomainPreference(landingPageDomainPreferenceFromProps)
            }}
            negativeMessage="Cancel"
            onConfirm={handleCustomDomainConfirm}
          >
            <div className={styles.container}>
              <div className={styles.customInput}>
                <TextInput
                  label="Your domain"
                  value={customSubdomainPlusDomain || ''}
                  placeholder="brand.yourdomain.com"
                  onChange={e => {
                    setCustomSubdomainPlusDomain(e.target.value)
                  }}
                  className={styles.inputText}
                />
              </div>
            </div>
          </ConfirmationDialog>
        )}
      </div>
    </ConfirmationDialog>
  )
}

export default EditSubDomainControlDialog
